/**
 * COMANDOS DE ORDEM do OrdersRouter — o wire de trading (order-domain 🔴🔴):
 * placeOrder / cancelOrder / replaceOrder / cancelAll* / releaseAll* /
 * invert-partial / normalizeNew / lookupPositionRemark / tradability.
 * Extraído de ordersRouter.jsx na modularização dos god-objects; corpos
 * preservados byte-a-byte, `this` → `rt`; chamadas internas via `rt.<método>`
 * (delegates) — o singleton, os guards e o protocolo com o RouterHub são
 * EXATAMENTE os mesmos.
 *
 * Invariantes (não violar):
 * - Entry/modify passam por rt.gateAndResolveFuturesOrder (chokepoint I1
 *   gated 08:55); CANCEL usa rt.adjustFutSymbol (não-gated) — sair de posição
 *   nunca é bloqueado por símbolo stale.
 * - Escada de guards antes de qualquer invoke: tradingDisabled → gate →
 *   lote → inTradeState → isConnectionOK.
 * Contrato pinado ANTES da extração: routerOrderCommandsContract.test.js
 * (métodos de hub, payloads, remarks, escada de guards, invariante de cancel).
 */
import logger from "../../utils/logger.js";
import { playSound } from "../../utils/soundUtils.js";
import { globalToast } from "../../components/ToastProvider.jsx";
import { getOrderSideText } from "../../utils/orderStyles.js";
import {
  isTradingDisabled,
  getTradingDisabledReason,
} from "../../utils/mobileDetection.js";
import marketdata from "./marketdata.jsx";

// Extract OTO gain/loss legs (gain = limit sem stop; loss = tem stop) para
// logging — era 3 blocos copiados à mão; editar o predicado num lugar só
// mantém otoLogData e o modEntry reportado em sincronia (auditoria 3b).
const extractOtoLegs = (order) => {
  const out = {
    gainLeg: undefined,
    lossLeg: undefined,
    gainPrice: undefined,
    lossPrice: undefined,
    otoLogText: "",
    otoLogData: {},
  };
  if (Array.isArray(order.oto) && order.oto.length > 0) {
    out.gainLeg = order.oto.find((leg) => leg.limit && !leg.stop);
    out.lossLeg = order.oto.find((leg) => leg.stop);
    if (out.gainLeg) {
      out.gainPrice = out.gainLeg.limit;
      out.otoLogData.gainPrice = out.gainLeg.limit;
    }
    if (out.lossLeg) {
      out.lossPrice = out.lossLeg.stop;
      out.otoLogData.lossPrice = out.lossLeg.stop;
    }
    const parts = [];
    if (out.gainLeg) parts.push(`Gain: ${out.gainLeg.limit}`);
    if (out.lossLeg) parts.push(`Loss: ${out.lossLeg.stop}`);
    if (parts.length > 0) out.otoLogText = ` | OTO [${parts.join(", ")}]`;
  }
  return out;
};

export const placeOrder = async (rt, order) => {
  // Safety check: Block orders during replay mode or on mobile phones
  if (isTradingDisabled()) {
    const reason = getTradingDisabledReason();
    const message =
      reason === "mobile"
        ? "⚠️ Negociação não disponível em celulares"
        : "⚠️ Ordens bloqueadas durante modo de replay";
    const error = new Error(message);
    logger.orders.error("Tentativa de ordem bloqueada", {
      symbol: order.symbol,
      quantity: order.quantity,
      side: order.side,
      reason: reason,
    });
    globalToast.showError(message);
    throw error;
  }

  rt.gateAndResolveFuturesOrder(order);
  rt.adjustOrderPrices(order);
  order.remark = "so" + rt.lookupPositionRemark(order.symbol);

  if (rt.checkStandardLot(order.symbol, order.quantity) == false) {
    throw new Error("Quantidade inválida");
  }

  if (rt.inTradeState() == false) {
    throw new Error(
      "Cheque a conexão com o marketdata e com o roteador primeiro.",
    );
  }

  if (rt.isConnectionOK() == false) {
    throw new Error("Conecte-se ao roteador primeiro!");
  }

  const par = JSON.stringify(order);

  // [OTODiag] Capture exact placeOrder payload for chart-vs-book OTO comparison.
  // idShape auto-tags source: book uses crypto.randomUUID(), chart uses a composite id.
  try {
    logger.diagnostic.info("[OTODiag] placeOrder payload", {
      orderId: order.id,
      idShape: /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-/i.test(order.id || "")
        ? "uuid (book)"
        : "composite (chart)",
      symbol: order.symbol,
      action: order.action,
      quantity: order.quantity,
      tif: order.tif,
      limit: order.limit ?? null,
      stop: order.stop ?? null,
      remark: order.remark,
      hasOto: Array.isArray(order.oto),
      otoCount: Array.isArray(order.oto) ? order.oto.length : 0,
      oto: order.oto || null,
      rawPayload: par,
    });
  } catch {
    /* diagnostic best-effort */
  }

  const ret = await rt.signalRConnection.invoke("placeOrder", par);

  // Extract OTO gain/loss prices for logging
  const { otoLogText, otoLogData } = extractOtoLegs(order);

  if (ret == false) {
    const orderPrice = order.limit || order.stop || "MERCADO";
    const orderSide = (
      order.side ||
      order.type ||
      order.action ||
      "BUY"
    ).toUpperCase();
    // Translate order side to Portuguese
    const orderSideTranslated = getOrderSideText(orderSide.toLowerCase());
    const logMessage = `Falha ao enviar ordem: ${orderSideTranslated} ${order.symbol} - ${order.quantity} @ ${orderPrice}${otoLogText}`;

    logger.orders.error(logMessage, {
      symbol: order.symbol || "N/A",
      price: orderPrice,
      quantity: order.quantity || "N/A",
      orderType: orderSide,
      orderCategory: order.oco
        ? "Stop"
        : order.limit
          ? "Limitada"
          : order.stop
            ? "Stop"
            : "Mercado",
      broker: rt.broker,
      reason: "Falha na submissão da ordem",
      timestamp: new Date().toISOString(),
      ...otoLogData,
    });
    playSound("bump", 0.3);
    throw new Error("falha ao enviar ordem");
  } else {
    const orderPrice = order.limit || order.stop || "MERCADO";
    const orderSide = (
      order.side ||
      order.type ||
      order.action ||
      "BUY"
    ).toUpperCase();
    // Translate order side to Portuguese
    const orderSideTranslated = getOrderSideText(orderSide.toLowerCase());
    const logMessage = `Ordem enviada com sucesso: ${orderSideTranslated} ${order.symbol} - ${order.quantity} @ ${orderPrice}${otoLogText}`;

    logger.orders.success(logMessage, {
      symbol: order.symbol || "N/A",
      price: orderPrice,
      quantity: order.quantity || "N/A",
      orderType: orderSide,
      orderCategory: order.oco
        ? "Stop"
        : order.limit
          ? "Limitada"
          : order.stop
            ? "Stop"
            : "Mercado",
      broker: rt.broker,
      orderId: typeof ret === "boolean" ? order.id || "Pendente" : ret,
      timestamp: new Date().toISOString(),
      ...otoLogData,
    });
    playSound("ping", 0.3);

    // NOTE: Removed getAccountDataAsync() call - reports provide instant order updates
    // AccountData is only fetched on initial connection and reconnection
    return "OK";
  }
};

export const cancelOrder = async (rt, order) => {
  // Safety check: Block orders during replay mode or on mobile phones
  if (isTradingDisabled()) {
    const reason = getTradingDisabledReason();
    const message =
      reason === "mobile"
        ? "⚠️ Negociação não disponível em celulares"
        : "⚠️ Ordens bloqueadas durante modo de replay";
    const error = new Error(message);
    logger.orders.error("Tentativa de cancelamento de ordem bloqueada", {
      orderId: order.id,
      symbol: order.symbol,
      reason: reason,
    });
    globalToast.showError(message);
    throw error;
  }

  rt.adjustFutSymbol(order);
  order.remark = "do" + rt.lookupPositionRemark(order.symbol);
  var par = JSON.stringify(order);

  console.log("🚫 cancelOrder: Attempting to cancel order:", {
    id: order.id,
    symbol: order.symbol,
    state: order.state,
    orderStringLength: par.length,
  });

  if (!rt.inTradeState()) {
    throw new Error(
      "Cheque a conexão com o marketdata e com o roteador primeiro.",
    );
  }
  if (!rt.isConnectionOK()) {
    throw new Error("Conecte-se ao roteador primeiro!");
  }

  try {
    const ret = await rt.signalRConnection.invoke("cancelOrder", par);

    if (ret == false) {
      // Log detailed info about the failed cancellation for debugging
      console.error(
        "❌ cancelOrder: Router returned false. Order may not exist or already cancelled.",
        {
          orderId: order.id,
          symbol: order.symbol,
          orderState: order.state,
          originalRouterOrder: order.originalRouterOrder
            ? "present"
            : "missing",
        },
      );
      logger.orders.error("Falha no cancelamento da ordem", {
        symbol: order.symbol || "N/A",
        price: order.limit || order.stop || "MERCADO",
        quantity: order.quantity || "N/A",
        orderType: (order.side || order.action || "DESCONHECIDO").toUpperCase(),
        broker: rt.broker,
        orderId: order.id || "N/A",
        reason:
          "Falha ao cancelar ordem - ordem pode não existir no router ou já foi cancelada",
        timestamp: new Date().toISOString(),
      });
      throw new Error(
        "Falha ao enviar cancelamento. A ordem pode não existir mais ou já foi cancelada.",
      );
    }

    // #442: o retorno do invoke diz só que o roteador EMITIU o 35=F ao broker
    // (ConnectionManager.CancelOrderRequest → "OK" = enviado). Quem confirma é o
    // rpt seguinte — "cancelled" (39=4) ou "rejected" (OrderCancelReject) —, e é
    // ele que loga o desfecho e mostra o toast (routerReports.js). Aqui só fica o
    // rastro do envio: declarar sucesso neste ponto contradizia a ordem que segue
    // viva no book quando o broker rejeita ou nunca responde.
    logger.orders.info("Cancelamento enviado ao roteador", {
      symbol: order.symbol || "N/A",
      price: order.limit || order.stop || "MERCADO",
      quantity: order.quantity || "N/A",
      orderType: (order.side || order.action || "DESCONHECIDO").toUpperCase(),
      broker: rt.broker,
      orderId: order.id || "N/A",
      timestamp: new Date().toISOString(),
    });

    return "OK";
  } catch (err) {
    throw new Error(err.toString());
  }
};
export const lookupPositionRemark = (rt, symbol) => {
  let net = 0;
  let q = 0;
  let p = 0;
  if (rt.accountData && rt.accountData.positions) {
    let a = rt.accountData.positions[symbol];
    if (a && a.basis) {
      net = a.basis;
    }
    if (a && a.quantity) {
      q = a.quantity;
    }
    if (a && a.price) {
      p = a.price;
    }
  }
  return "(S:" + net + ";Q:" + q + ";P:" + p + ")";
};
// Núcleo compartilhado dos comandos All/ForSymbol: escada de guards →
// invoke → ret==false ⇒ logger.error+throw → logger.info → refresh de
// accountData → "OK". Extraído de 4 corpos idênticos (auditoria 3c);
// wire pinado por routerOrderCommandsContract.test.js. `prepare` roda
// DEPOIS dos guards (fidelidade: translateFutSymbol fora do try, pós-guard).
const invokeRouterCommand = async (
  rt,
  { method, prepare, failMessage, successMessage, afterLabel },
) => {
  if (!rt.inTradeState()) {
    throw new Error(
      "Cheque a conexão com o marketdata e com o roteador primeiro.",
    );
  }
  if (!rt.isConnectionOK()) {
    throw new Error("Conecte-se ao roteador primeiro!");
  }

  const { args = [], logData = {} } = prepare ? prepare() : {};

  try {
    const ret = await rt.signalRConnection.invoke(method, ...args);

    if (ret == false) {
      logger.orders.error(failMessage, {
        ...logData,
        broker: rt.broker,
        reason: "Falha ao enviar comando",
        timestamp: new Date().toISOString(),
      });
      throw new Error("falha ao enviar comando");
    }

    logger.orders.info(successMessage, {
      ...logData,
      broker: rt.broker,
      timestamp: new Date().toISOString(),
    });

    console.log(`📞 Router: Calling getAccountDataAsync after ${afterLabel}`);
    rt.getAccountDataAsync();
    return "OK";
  } catch (err) {
    throw new Error(err.toString());
  }
};

export const cancelAllOrders = (rt) =>
  // logger padronizado com os 3 irmãos (era o único sem — drift da auditoria 3c)
  invokeRouterCommand(rt, {
    method: "cancelAllOrders",
    afterLabel: "cancelAllOrders",
    failMessage: "Falha ao cancelar todas as ordens",
    successMessage: "Todas as ordens foram canceladas",
  });

export const cancelAllOrdersForSymbol = (rt, symbol) =>
  invokeRouterCommand(rt, {
    method: "cancelAllOrdersForSymbol2",
    afterLabel: "cancelAllOrdersForSymbol",
    failMessage: "Falha ao cancelar todas as ordens do símbolo",
    successMessage: "Todas as ordens do símbolo foram canceladas",
    prepare: () => {
      const resolved = rt.translateFutSymbol(symbol);
      return {
        args: [resolved, "caos" + rt.lookupPositionRemark(resolved)],
        logData: { symbol: resolved || "N/A" },
      };
    },
  });

export const releaseAllPositions = (rt) =>
  invokeRouterCommand(rt, {
    method: "releaseAllPositions",
    afterLabel: "releaseAllPositions",
    failMessage: "Falha ao encerrar todas as posições",
    successMessage: "Todas as posições foram encerradas",
  });

export const releaseAllPositionsForSymbol = (rt, symbol) =>
  invokeRouterCommand(rt, {
    method: "releaseAllPositionsForSymbol2",
    afterLabel: "releaseAllPositionsForSymbol",
    failMessage: "FALHA AO ENCERRAR POSIÇÕES DO SÍMBOLO",
    successMessage: "POSIÇÕES DO SÍMBOLO ENCERRADAS",
    prepare: () => {
      const resolved = rt.translateFutSymbol(symbol);
      return {
        args: [resolved, "raps" + rt.lookupPositionRemark(resolved)],
        logData: { symbol: resolved || "N/A" },
      };
    },
  });

export const invertPositionForSymbol = (rt, symbol) => {
  // Log position operations with descriptive messages
  logger.orders.info("Inversão de posição solicitada", {
    symbol: symbol || "N/A",
    broker: rt.broker,
    timestamp: new Date().toISOString(),
  });
};

export const partialPositionForSymbol = (rt, symbol) => {
  // TODO: Implement partial position logic
  logger.orders.info("Posição parcial solicitada", {
    symbol: symbol || "N/A",
    broker: rt.broker,
    timestamp: new Date().toISOString(),
  });
};

export const clone = (rt, obj) => {
  if (null == obj || "object" != typeof obj) return obj;
  var copy = obj.constructor();
  for (var attr in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, attr)) copy[attr] = obj[attr];
  }
  return copy;
};

export const isEmptyObj = (rt, obj) => {
  if (obj === null) return true;
  return Object.keys(obj).length === 0;
};
export const normalizeNew = (rt, order) => {
  Object.keys(order).forEach((key) => {
    // if (rt.isEmptyObj(order[key]))
    //     order[key] = null;
    if (order[key] && order[key].new) {
      order[key] = order[key].new;
    }
    // Only null-ify oto when it's an explicit {old,new} wrapper with new==null
    // (signal to remove OTO legs). Plain arrays must be preserved.
    if (
      key == "oto" &&
      order[key] &&
      !Array.isArray(order[key]) &&
      order[key].new == null
    ) {
      order[key] = null;
    }
  });
  return order;
};
export const isObject = (rt, obj) => {
  return obj === Object(obj);
};
export const replaceOrder = async (rt, order) => {
  // Safety check: Block orders during replay mode or on mobile phones
  if (isTradingDisabled()) {
    const reason = getTradingDisabledReason();
    const message =
      reason === "mobile"
        ? "⚠️ Negociação não disponível em celulares"
        : "⚠️ Ordens bloqueadas durante modo de replay";
    const error = new Error(message);
    logger.orders.error("Tentativa de modificação de ordem bloqueada", {
      orderId: order.id,
      symbol: order.symbol,
      reason: reason,
    });
    globalToast.showError(message);
    throw error;
  }

  if (import.meta.env.DEV) {
    try {
      console.log(
        "🔧 [replaceOrder] Incoming (raw) order:",
        JSON.stringify(order, null, 2),
      );
    } catch {
      /* logging serialization failed */
    }
  }

  if (order.id && (order.id.endsWith("-L") || order.id.endsWith("-S"))) {
    // FIX limit x stop price error
    // Guard against null/undefined and plain values (buildReplaceOrder sends plain numbers, not {old,new})
    if (order.id.endsWith("-L")) {
      if (
        order.limit &&
        order.limit.new === undefined &&
        order.stop &&
        order.stop.new !== undefined
      ) {
        order.limit.new = order.stop.new;
      }
      order.stop = null;
    } else if (order.id.endsWith("-S")) {
      if (
        order.stop &&
        order.stop.new === undefined &&
        order.limit &&
        order.limit.new !== undefined
      ) {
        order.stop.new = order.limit.new;
      }
      order.limit = null;
    }

    let orders =
      rt.accountData.openOrders && rt.accountData.openOrders[order.symbol];
    if (!orders) {
      console.warn(`No open orders found for symbol: ${order.symbol}`);
      return order;
    }
    let mainId = order.id.substr(0, order.id.length - 2);
    for (let x = 0; x < orders.length; x++) {
      if (orders[x].id == mainId) {
        let o = rt.clone(orders[x]);
        for (let y = 0; y < o.oto.length; y++) {
          if (o.oto[y].id === order.id) {
            let updOrder = rt.clone(order);
            updOrder = rt.normalizeNew(updOrder);
            o.oto[y] = updOrder;
            // Sync quantity to all sibling OTO/OCO legs
            // (e.g. editing loss qty must also update gain qty)
            for (let z = 0; z < o.oto.length; z++) {
              if (z !== y) {
                o.oto[z].quantity = updOrder.quantity;
              }
            }
            o = rt.normalizeNew(o);
            order = o;
            break;
          }
        }
        break;
      }
    }
  }

  if (order.limit && order.limit.new) {
    order.limit = order.limit.new;
  } else if (rt.isObject(order.limit)) {
    order.limit = null;
  }

  if (order.stop && order.stop.new) {
    order.stop = order.stop.new;
  } else if (rt.isObject(order.stop)) {
    order.stop = null;
  }
  if (order.quantity && order.quantity.new) {
    order.quantity = order.quantity.new;
  }

  if (order.tif && order.tif.new) {
    order.tif = order.tif.new;
  }

  if (order.oto && order.oto.new) {
    order.oto = order.oto.new;
  } else if (order.oto && order.oto.new === null && order.oto.old === null)
    order.oto = null;

  // Gate AFTER unwrapping the {old,new} oto wrapper, so `order.oto` is the
  // plain leg array — otherwise wrapper-shaped legs would skip the gate.
  rt.gateAndResolveFuturesOrder(order);

  // Skip re-normalization if buildReplaceOrder already normalized prices
  if (!order._normalized) {
    rt.adjustOrderPrices(order);
  }
  delete order._normalized; // Clean up marker before sending to router
  order.remark = "ro" + rt.lookupPositionRemark(order.symbol);
  var par = JSON.stringify(order);
  if (import.meta.env.DEV) {
    try {
      console.log(
        "🧪 [replaceOrder] Normalized (pre-serialize) order:",
        JSON.stringify(order, null, 2),
      );
    } catch {
      /* logging serialization failed */
    }
    console.log("📤 [replaceOrder] Serialized payload:", par);
  }

  if (rt.checkStandardLot(order.symbol, order.quantity) == false) {
    throw new Error("Quantidade inválida");
  }

  if (!rt.inTradeState()) {
    throw new Error(
      "Cheque a conexão com o marketdata e com o roteador primeiro.",
    );
  }
  if (!rt.isConnectionOK()) {
    throw new Error("Conecte-se ao roteador primeiro!");
  }

  // Log before sending to router for deterministic order
  const symbol = order.symbol || "N/A";
  const side = (order.side || order.action || "DESCONHECIDO").toUpperCase();
  let changeDetails = [];

  // Show what we're about to change
  const currentPrice = order.limit || order.stop || order.price;
  if (currentPrice) {
    const priceType = order.oco
      ? "stop"
      : order.limit
        ? "limit"
        : order.stop
          ? "stop"
          : "preço";
    changeDetails.push(`${priceType}: ${currentPrice}`);
  }

  if (order.quantity) {
    changeDetails.push(`qtt: ${order.quantity}`);
  }

  // Extract OTO gain/loss prices for logging
  const { otoLogText, otoLogData } = extractOtoLegs(order);

  const changeText =
    changeDetails.length > 0 ? ` (${changeDetails.join(", ")})` : "";
  // Translate order side to Portuguese
  const sideTranslated = getOrderSideText(side.toLowerCase());
  const preLogMessage = `Enviando modificação de ordem: ${sideTranslated} ${symbol}${changeText}${otoLogText}`;

  logger.orders.info(preLogMessage, {
    symbol: order.symbol || "N/A",
    orderId: order.id || "N/A",
    side: side,
    quantity: order.quantity || "N/A",
    price: currentPrice || "N/A",
    orderType: order.oco
      ? "STOP"
      : order.limit
        ? "LIMIT"
        : order.stop
          ? "STOP"
          : "MARKET",
    broker: rt.broker,
    timestamp: new Date().toISOString(),
    ...otoLogData,
  });

  try {
    const ret = await rt.signalRConnection.invoke("replaceOrder", par);
    if (import.meta.env.DEV) {
      console.log("📥 [replaceOrder] Immediate invoke return:", ret);
    }

    if (ret == false) {
      // Log the failure to the log panel
      const failSymbol = order.symbol || "N/A";
      const failSide = (
        order.side ||
        order.action ||
        "DESCONHECIDO"
      ).toUpperCase();
      const failSideTranslated = getOrderSideText(failSide.toLowerCase());
      const failPrice = order.limit || order.stop || order.price;
      logger.orders.error(
        `Falha ao modificar ordem: ${failSideTranslated} ${failSymbol} (preço: ${failPrice || "N/A"})${otoLogText}`,
        {
          symbol: failSymbol,
          orderId: order.id || "N/A",
          side: failSide,
          price: failPrice,
          broker: rt.broker,
          reason: "Router returned false",
          rawOrderData: JSON.stringify(order),
          ...otoLogData,
        },
      );
      throw new Error("falha ao enviar alteração");
    }

    // Track this modification for better logging
    const modificationPrice = order.price || order.limit || order.stop;

    if (modificationPrice) {
      // Store modification data for logging purposes (enriches replaced reports)
      const modEntry = {
        symbol: order.symbol,
        price: modificationPrice,
        side: order.action || order.side,
        quantity: order.quantity,
        timestamp: Date.now(),
      };
      // Include OTO gain/loss prices if present
      const { gainPrice, lossPrice } = extractOtoLegs(order);
      if (gainPrice !== undefined) modEntry.gainPrice = gainPrice;
      if (lossPrice !== undefined) modEntry.lossPrice = lossPrice;
      rt.recentModifications.push(modEntry);

      // Clean up old modifications (older than 30 seconds)
      rt.recentModifications = rt.recentModifications.filter(
        (mod) => Date.now() - mod.timestamp <= 30000,
      );
    }

    // Success confirmation log (this comes after router confirms)
    // Re-build message details for success log
    const successSymbol = order.symbol || "N/A";
    const successSide = (
      order.side ||
      order.action ||
      "DESCONHECIDO"
    ).toUpperCase();
    let successChangeDetails = [];

    const successPrice = order.limit || order.stop || order.price;
    if (successPrice) {
      const priceType = order.oco
        ? "stop"
        : order.limit
          ? "limit"
          : order.stop
            ? "stop"
            : "preço";
      successChangeDetails.push(`${priceType}: ${successPrice}`);
    }

    if (order.quantity) {
      successChangeDetails.push(`qtt: ${order.quantity}`);
    }

    const successChangeText =
      successChangeDetails.length > 0
        ? ` (${successChangeDetails.join(", ")})`
        : "";
    const successSideTranslated = getOrderSideText(successSide.toLowerCase());
    const successLogMessage = `Modificação de ordem confirmada: ${successSideTranslated} ${successSymbol}${successChangeText}${otoLogText}`;

    logger.orders.success(successLogMessage, {
      symbol: order.symbol || "N/A",
      orderId: order.id || "N/A",
      side: (order.side || order.action || "DESCONHECIDO").toUpperCase(),
      quantity: order.quantity?.new || order.quantity || "N/A",
      originalQuantity: order.quantity?.old || "N/A",
      price: order.price || order.limit || order.stop || "Mercado",
      originalPrice: "N/A", // Not available in current structure
      orderType: order.oco
        ? "STOP"
        : order.limit?.new || order.limit
          ? "LIMIT"
          : order.stop?.new || order.stop
            ? "STOP"
            : "MARKET",
      broker: rt.broker,
      timestamp: new Date().toISOString(),
      rawOrderData: JSON.stringify(order),
      ...otoLogData,
    });
    playSound("ping", 0.3);
    return "OK";
  } catch (error) {
    // Log the error to the log panel
    const errSymbol = order.symbol || "N/A";
    const errSide = (
      order.side ||
      order.action ||
      "DESCONHECIDO"
    ).toUpperCase();
    const errSideTranslated = getOrderSideText(errSide.toLowerCase());
    const errPrice = order.limit || order.stop || order.price;
    logger.orders.error(
      `Erro ao modificar ordem: ${errSideTranslated} ${errSymbol} (preço: ${errPrice || "N/A"})${otoLogText} - ${error.message || error}`,
      {
        symbol: errSymbol,
        orderId: order.id || "N/A",
        side: errSide,
        price: errPrice,
        broker: rt.broker,
        error: error.message || error.toString(),
        rawOrderData: JSON.stringify(order),
        ...otoLogData,
      },
    );
    throw new Error(error.toString());
  }
};

export const tradability = (rt, symbol) => {
  if (marketdata.symbols && marketdata.symbols.has(symbol)) {
    var si = marketdata.symbols.get(symbol);
    if (si.F == "I") {
      return false;
    }
  }
  return true;
};
